/**
 * Organization blocks: TEAM, LOGOS, TABS
 * Blocks for displaying organizational content like team members,
 * partner logos, and tabbed content sections.
 */

import { registerBlockRenderer, renderBlock, getBlockRendererContext } from './registry';
import { escapeHtml, createBlockWrapper, isUrlSafe } from './utils';
import { renderIcon } from '../icons';
import type { TeamData, TeamMember, LogosData, TabsData, TabItem } from './types';

// =============================================================================
// TEAM BLOCK
// =============================================================================

registerBlockRenderer('team', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  const teamData = data as TeamData;
  const wrapper = createBlockWrapper('team');
  const layout = teamData.layout || 'grid';
  const compact = teamData.compact === true;

  let html = '';

  // Optional title
  if (teamData.title) {
    html += `<h3 class="pms-team-title">${escapeHtml(teamData.title)}</h3>`;
  }

  // Team members container
  html += `<div class="pms-team-members pms-team-${layout}${compact ? ' pms-team-compact' : ''}">`;

  if (teamData.members && teamData.members.length > 0) {
    for (const member of teamData.members) {
      html += renderTeamMember(member, compact);
    }
  }

  html += '</div>';
  wrapper.innerHTML = html;

  // Add action handlers
  if (onAction) {
    wrapper.querySelectorAll('[data-action]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const action = (el as HTMLElement).dataset.action;
        if (action) {
          onAction(action);
        }
      });
    });
  }

  // Vertical signature — decorative accent per vertical
  const vertical = getBlockRendererContext().vertical;

  if (vertical === 'saas' || vertical === 'tech') {
    wrapper.querySelectorAll('.pms-team-member').forEach(member => {
      const dot = document.createElement('div');
      dot.className = 'pms-team-status-dot';
      member.prepend(dot);
    });
  }

  if (vertical === 'restaurant') {
    wrapper.querySelectorAll('.pms-team-member').forEach(member => {
      const tag = document.createElement('div');
      tag.className = 'pms-team-staff-tag';
      tag.textContent = 'STAFF';
      member.prepend(tag);
    });
  }

  if (vertical === 'finance' || vertical === 'fintech') {
    wrapper.querySelectorAll('.pms-team-member').forEach(member => {
      const badge = document.createElement('div');
      badge.className = 'pms-team-credential-badge';
      member.prepend(badge);
    });
  }

  if (vertical === 'healthcare') {
    wrapper.querySelectorAll('.pms-team-member').forEach(member => {
      const cross = document.createElement('div');
      cross.className = 'pms-team-care-cross';
      member.prepend(cross);
    });
  }

  if (vertical === 'portfolio') {
    wrapper.querySelectorAll('.pms-team-member').forEach(member => {
      const mark = document.createElement('div');
      mark.className = 'pms-team-creative-mark';
      member.prepend(mark);
    });
  }

  if (vertical === 'agency') {
    wrapper.querySelectorAll('.pms-team-member').forEach(member => {
      const badge = document.createElement('div');
      badge.className = 'pms-team-campaign-badge';
      badge.textContent = 'CREW';
      member.prepend(badge);
    });
  }

  if (vertical === 'education') {
    wrapper.querySelectorAll('.pms-team-member').forEach(member => {
      const tag = document.createElement('div');
      tag.className = 'pms-team-faculty-tag';
      tag.textContent = 'FACULTY';
      member.prepend(tag);
    });
  }

  if (vertical === 'nonprofit') {
    wrapper.querySelectorAll('.pms-team-member').forEach(member => {
      const tag = document.createElement('div');
      tag.className = 'pms-team-volunteer-tag';
      tag.textContent = 'VOLUNTEER';
      member.prepend(tag);
    });
  }

  if (vertical === 'legal') {
    wrapper.querySelectorAll('.pms-team-member').forEach(member => {
      const tag = document.createElement('div');
      tag.className = 'pms-team-counsel-tag';
      tag.textContent = 'COUNSEL';
      member.prepend(tag);
    });
  }

  if (vertical === 'ecommerce') {
    wrapper.querySelectorAll('.pms-team-member').forEach(member => {
      const tag = document.createElement('div');
      tag.className = 'pms-team-shop-tag';
      tag.textContent = 'TEAM';
      member.prepend(tag);
    });
  }

  if (vertical === 'real_estate') {
    wrapper.querySelectorAll('.pms-team-member').forEach(member => {
      const tag = document.createElement('div');
      tag.className = 'pms-team-agent-tag';
      tag.textContent = 'AGENT';
      member.prepend(tag);
    });
  }

  if (vertical === 'automotive') {
    wrapper.querySelectorAll('.pms-team-member').forEach(member => {
      const tag = document.createElement('div');
      tag.className = 'pms-team-specialist-tag';
      tag.textContent = 'SPECIALIST';
      member.prepend(tag);
    });
  }

  if (vertical === 'professional_services') {
    wrapper.querySelectorAll('.pms-team-member').forEach(member => {
      const tag = document.createElement('div');
      tag.className = 'pms-team-advisor-tag';
      tag.textContent = 'ADVISOR';
      member.prepend(tag);
    });
  }

  if (vertical === 'dental') {
    wrapper.querySelectorAll('.pms-team-member').forEach(member => {
      const tag = document.createElement('div');
      tag.className = 'pms-team-doctor-tag';
      tag.textContent = 'DOCTOR';
      member.prepend(tag);
    });
  }

  if (vertical === 'fun') {
    wrapper.querySelectorAll('.pms-team-member').forEach(member => {
      const tag = document.createElement('div');
      tag.className = 'pms-team-hacker-tag';
      tag.textContent = 'HACKER';
      member.prepend(tag);
    });
  }

  container.appendChild(wrapper);
});

function renderTeamMember(member: TeamMember & { avatar?: string }, compact: boolean): string {
  let html = `<div class="pms-team-member">`;

  // Avatar/image - support both image (widget) and avatar (backend) field names
  const imageUrl = member.image || member.avatar;
  if (imageUrl && isUrlSafe(imageUrl)) {
    html += `<div class="pms-team-avatar">
      <img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(member.name)}" loading="lazy" decoding="async" />
    </div>`;
  } else {
    // Placeholder avatar with initials
    const initials = member.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    html += `<div class="pms-team-avatar pms-team-avatar-placeholder">
      <span>${escapeHtml(initials)}</span>
    </div>`;
  }

  // Member info
  html += `<div class="pms-team-info">`;
  html += `<div class="pms-team-name">${escapeHtml(member.name)}</div>`;

  if (member.role) {
    html += `<div class="pms-team-role">${escapeHtml(member.role)}</div>`;
  }

  if (!compact && member.bio) {
    html += `<div class="pms-team-bio">${escapeHtml(member.bio)}</div>`;
  }

  // Social links
  if (member.social) {
    html += '<div class="pms-team-social">';
    if (member.social.linkedin && isUrlSafe(member.social.linkedin)) {
      html += `<a href="${escapeHtml(member.social.linkedin)}" target="_blank" rel="noopener noreferrer" class="pms-team-social-link" aria-label="LinkedIn">
        <svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/></svg>
      </a>`;
    }
    if (member.social.twitter && isUrlSafe(member.social.twitter)) {
      html += `<a href="${escapeHtml(member.social.twitter)}" target="_blank" rel="noopener noreferrer" class="pms-team-social-link" aria-label="Twitter">
        <svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M22.46 6c-.85.38-1.78.64-2.75.76 1-.6 1.76-1.55 2.12-2.68-.93.55-1.96.95-3.06 1.17-.88-.94-2.13-1.53-3.51-1.53-2.66 0-4.81 2.16-4.81 4.81 0 .38.04.75.13 1.1-4-.2-7.58-2.11-9.96-5.02-.42.72-.66 1.56-.66 2.46 0 1.68.85 3.16 2.14 4.02-.79-.02-1.53-.24-2.18-.6v.06c0 2.35 1.67 4.31 3.88 4.76-.4.1-.83.16-1.27.16-.31 0-.62-.03-.92-.08.63 1.96 2.45 3.39 4.61 3.43-1.69 1.32-3.83 2.1-6.15 2.1-.4 0-.8-.02-1.19-.07 2.19 1.4 4.78 2.22 7.57 2.22 9.07 0 14.02-7.52 14.02-14.02 0-.21 0-.42-.01-.63.96-.69 1.79-1.56 2.45-2.55z"/></svg>
      </a>`;
    }
    if (member.social.email) {
      html += `<a href="mailto:${escapeHtml(member.social.email)}" class="pms-team-social-link" aria-label="Email">
        <svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
      </a>`;
    }
    if (member.social.phone) {
      html += `<a href="tel:${escapeHtml(member.social.phone)}" class="pms-team-social-link" aria-label="Phone">
        <svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
      </a>`;
    }
    html += '</div>';
  }

  // Action button
  if (member.action) {
    html += `<button class="pms-team-action" data-action="${escapeHtml(member.action.value)}">${escapeHtml(member.action.label)}</button>`;
  }

  html += '</div>'; // .pms-team-info
  html += '</div>'; // .pms-team-member

  return html;
}

// =============================================================================
// LOGOS BLOCK
// =============================================================================

registerBlockRenderer('logos', (data: unknown, container: HTMLElement) => {
  const logosData = data as LogosData;
  const wrapper = createBlockWrapper('logos');
  const grayscale = logosData.grayscale !== false; // Default true
  const size = logosData.size || 'medium';

  let html = '';

  // Optional title
  if (logosData.title) {
    html += `<div class="pms-logos-title">${escapeHtml(logosData.title)}</div>`;
  }

  // Logos container
  html += `<div class="pms-logos-grid pms-logos-${size}${grayscale ? ' pms-logos-grayscale' : ''}">`;

  if (logosData.logos && logosData.logos.length > 0) {
    for (const logo of logosData.logos) {
      if (!logo.image || !isUrlSafe(logo.image)) continue;

      if (logo.url && isUrlSafe(logo.url)) {
        html += `<a href="${escapeHtml(logo.url)}" target="_blank" rel="noopener noreferrer" class="pms-logo-item">
          <img src="${escapeHtml(logo.image)}" alt="${escapeHtml(logo.name)}" loading="lazy" decoding="async" />
        </a>`;
      } else {
        html += `<div class="pms-logo-item">
          <img src="${escapeHtml(logo.image)}" alt="${escapeHtml(logo.name)}" loading="lazy" decoding="async" />
        </div>`;
      }
    }
  }

  html += '</div>';
  wrapper.innerHTML = html;

  // Vertical signature — decorative accent per vertical
  const vertical = getBlockRendererContext().vertical;

  if (vertical === 'saas' || vertical === 'tech') {
    const label = document.createElement('div');
    label.className = 'pms-logos-integration-label';
    label.textContent = 'INTEGRATIONS';
    wrapper.prepend(label);
  }

  if (vertical === 'finance' || vertical === 'fintech') {
    const label = document.createElement('div');
    label.className = 'pms-logos-partner-label';
    label.textContent = 'TRUSTED BY';
    wrapper.prepend(label);
  }

  if (vertical === 'agency') {
    const label = document.createElement('div');
    label.className = 'pms-logos-client-label';
    label.textContent = 'CLIENTS';
    wrapper.prepend(label);
  }

  if (vertical === 'education') {
    const label = document.createElement('div');
    label.className = 'pms-logos-partners-label';
    label.textContent = 'PARTNERS';
    wrapper.prepend(label);
  }

  if (vertical === 'nonprofit') {
    const label = document.createElement('div');
    label.className = 'pms-logos-supporters-label';
    label.textContent = 'SUPPORTERS';
    wrapper.prepend(label);
  }

  if (vertical === 'legal') {
    const label = document.createElement('div');
    label.className = 'pms-logos-affiliates-label';
    label.textContent = 'AFFILIATES';
    wrapper.prepend(label);
  }

  if (vertical === 'restaurant') {
    const label = document.createElement('div');
    label.className = 'pms-logos-purveyor-label';
    label.textContent = 'PURVEYORS';
    wrapper.prepend(label);
  }

  if (vertical === 'ecommerce') {
    const label = document.createElement('div');
    label.className = 'pms-logos-brand-label';
    label.textContent = 'BRANDS';
    wrapper.prepend(label);
  }

  if (vertical === 'healthcare') {
    const label = document.createElement('div');
    label.className = 'pms-logos-network-label';
    label.textContent = 'NETWORK';
    wrapper.prepend(label);
  }

  if (vertical === 'real_estate') {
    const label = document.createElement('div');
    label.className = 'pms-logos-realty-label';
    label.textContent = 'PARTNERS';
    wrapper.prepend(label);
  }

  if (vertical === 'automotive') {
    const label = document.createElement('div');
    label.className = 'pms-logos-auto-label';
    label.textContent = 'BRANDS';
    wrapper.prepend(label);
  }

  if (vertical === 'portfolio') {
    const label = document.createElement('div');
    label.className = 'pms-logos-client-portfolio-label';
    label.textContent = 'CLIENTS';
    wrapper.prepend(label);
  }

  if (vertical === 'professional_services') {
    const label = document.createElement('div');
    label.className = 'pms-logos-ps-label';
    label.textContent = 'CLIENTS';
    wrapper.prepend(label);
  }

  if (vertical === 'dental') {
    const label = document.createElement('div');
    label.className = 'pms-logos-dental-label';
    label.textContent = 'PARTNERS';
    wrapper.prepend(label);
  }

  if (vertical === 'fun') {
    const label = document.createElement('div');
    label.className = 'pms-logos-sponsor-label';
    label.textContent = 'SPONSORS';
    wrapper.prepend(label);
  }

  container.appendChild(wrapper);
});

// =============================================================================
// TABS BLOCK
// =============================================================================

registerBlockRenderer('tabs', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  const tabsData = data as TabsData;
  const wrapper = createBlockWrapper('tabs');
  const style = tabsData.style || 'line';

  if (!tabsData.tabs || tabsData.tabs.length === 0) {
    wrapper.innerHTML = '<div class="pms-tabs-empty">No tabs defined</div>';
    container.appendChild(wrapper);
    return;
  }

  // Determine default active tab
  const defaultTabId = tabsData.defaultTab || tabsData.tabs[0]?.id;

  // Tab buttons
  let html = `<div class="pms-tabs-header pms-tabs-${style}" role="tablist">`;

  for (const tab of tabsData.tabs) {
    const isActive = tab.id === defaultTabId;
    html += `<button
      class="pms-tab-button${isActive ? ' pms-tab-active' : ''}"
      role="tab"
      aria-selected="${isActive}"
      aria-controls="pms-tabpanel-${escapeHtml(tab.id)}"
      data-tab-id="${escapeHtml(tab.id)}"
    >`;
    if (tab.icon) {
      const iconSvg = renderIcon(tab.icon);
      html += `<span class="pms-tab-icon">${iconSvg || escapeHtml(tab.icon)}</span>`;
    }
    html += `<span class="pms-tab-label">${escapeHtml(tab.label)}</span>`;
    html += '</button>';
  }

  html += '</div>';

  // Tab panels
  html += '<div class="pms-tabs-content">';

  for (const tab of tabsData.tabs) {
    const isActive = tab.id === defaultTabId;
    html += `<div
      class="pms-tab-panel${isActive ? ' pms-tab-panel-active' : ''}"
      role="tabpanel"
      id="pms-tabpanel-${escapeHtml(tab.id)}"
      data-tab-id="${escapeHtml(tab.id)}"
      ${!isActive ? 'hidden' : ''}
    ></div>`;
  }

  html += '</div>';
  wrapper.innerHTML = html;

  // Render tab content (after DOM is created)
  for (const tab of tabsData.tabs) {
    const panel = wrapper.querySelector(`[id="pms-tabpanel-${tab.id}"]`) as HTMLElement;
    if (panel) {
      renderTabContent(tab, panel, onAction);
    }
  }

  // Add tab switching logic
  const buttons = wrapper.querySelectorAll('.pms-tab-button');
  const panels = wrapper.querySelectorAll('.pms-tab-panel');

  buttons.forEach(button => {
    button.addEventListener('click', () => {
      const tabId = (button as HTMLElement).dataset.tabId;

      // Update buttons
      buttons.forEach(btn => {
        const isActive = (btn as HTMLElement).dataset.tabId === tabId;
        btn.classList.toggle('pms-tab-active', isActive);
        btn.setAttribute('aria-selected', String(isActive));
      });

      // Update panels
      panels.forEach(panel => {
        const isActive = (panel as HTMLElement).dataset.tabId === tabId;
        panel.classList.toggle('pms-tab-panel-active', isActive);
        if (isActive) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });
    });

    // Keyboard navigation
    button.addEventListener('keydown', (e: Event) => {
      const keyEvent = e as KeyboardEvent;
      const buttonsArray = Array.from(buttons);
      const currentIndex = buttonsArray.indexOf(button);
      let newIndex = currentIndex;

      if (keyEvent.key === 'ArrowRight' || keyEvent.key === 'ArrowDown') {
        newIndex = (currentIndex + 1) % buttonsArray.length;
      } else if (keyEvent.key === 'ArrowLeft' || keyEvent.key === 'ArrowUp') {
        newIndex = (currentIndex - 1 + buttonsArray.length) % buttonsArray.length;
      } else if (keyEvent.key === 'Home') {
        newIndex = 0;
      } else if (keyEvent.key === 'End') {
        newIndex = buttonsArray.length - 1;
      }

      if (newIndex !== currentIndex) {
        keyEvent.preventDefault();
        (buttonsArray[newIndex] as HTMLElement).focus();
        (buttonsArray[newIndex] as HTMLElement).click();
      }
    });
  });

  container.appendChild(wrapper);
});

function renderTabContent(tab: TabItem, panel: HTMLElement, onAction?: (value: string) => void): void {
  if (!tab.content) return;

  if (tab.content.type === 'text' && tab.content.text) {
    panel.innerHTML = `<div class="pms-tab-text">${escapeHtml(tab.content.text)}</div>`;
  } else if (tab.content.type === 'blocks' && tab.content.blocks) {
    // Render nested blocks
    for (const block of tab.content.blocks) {
      renderBlock(block, panel, onAction);
    }
  }
}
